import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon, Info } from 'lucide-react';

import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import {
    orgMemberRoleDescription,
    orgMemberRoleLabel,
} from '@apps/web/lib/organization_permissions';

type RoleSelectItemProps = {
    readonly role: 'admin' | 'scheduler' | 'restricted_scheduler';
    readonly disabled?: boolean;
};

// A Select option for an org member role, with an info tooltip describing what
// the role can do. The icon is a sibling of ItemText (not inside it) so the
// closed trigger shows only the label — Radix mirrors ItemText into the
// trigger. The icon sits in a pointer-events-auto span so the tooltip still
// fires inside Select items, which force pointer-events:none on nested svgs and
// on disabled items.
export function RoleSelectItem({
    role,
    disabled = false,
}: RoleSelectItemProps) {
    const description = orgMemberRoleDescription(role);
    return (
        <SelectPrimitive.Item
            value={role}
            disabled={disabled}
            data-slot="select-item"
            className="focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
        >
            <span className="absolute right-2 flex size-3.5 items-center justify-center">
                <SelectPrimitive.ItemIndicator>
                    <CheckIcon className="size-4" />
                </SelectPrimitive.ItemIndicator>
            </span>
            <SelectPrimitive.ItemText>
                {orgMemberRoleLabel(role)}
            </SelectPrimitive.ItemText>
            {description ? (
                <Tooltip>
                    <TooltipTrigger asChild={true}>
                        <span
                            className="pointer-events-auto inline-flex"
                            tabIndex={-1}
                            onPointerDown={(event) => event.stopPropagation()}
                        >
                            <Info className="text-muted-foreground size-3.5" />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="right">{description}</TooltipContent>
                </Tooltip>
            ) : null}
        </SelectPrimitive.Item>
    );
}
